/**
 * The sign-in screen.
 *
 * ---------------------------------------------------------------------------
 * HOW THE WIDGET IS MOUNTED, AND WHY IMPERATIVELY
 * ---------------------------------------------------------------------------
 * Telegram's Login Widget is a `<script>` tag that REPLACES ITSELF with an
 * iframe when the browser executes it. React does not execute a `<script>`
 * element rendered as JSX — it inserts an inert node — so the tag is created
 * with `document.createElement` in an effect and appended to a container. That
 * is not a workaround; it is the only thing that works.
 *
 * ⚠ A `<script>` INSIDE A JSX COMMENT IS NOT A WIDGET. Until v-0-1-3 this file
 * carried the tag as a comment next to an empty `<div id="telegram-login-widget" />`,
 * which read like wiring and rendered nothing. `test/ui/login-widget.test.ts`
 * now asserts the URL survives into an EMITTED `.js` of a real `vite build`, so
 * a commented-out tag can never pass again.
 *
 * `data-onauth` is a string evaluated by Telegram's script in page scope, so the
 * callback has to be a real global. It is installed on `window` for the lifetime
 * of the effect and removed on cleanup.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';

import { ApiError, apiPost } from '../lib/api';
import { Card, Placeholder } from '../components/primitives';

/**
 * D17. The bot is `@mount_infra_bot` (id 8638419171) — the estate's alerting
 * bot, reused deliberately. BotFather `/setdomain` for
 * `watch.cloud.mountsoftware.ro` was run against THIS username; a different one
 * renders the literal string `Bot domain invalid` instead of a button.
 */
const BOT_USERNAME = 'mount_infra_bot';

/** Pinned revision, as Telegram's own documentation embeds it. */
const WIDGET_SRC = 'https://telegram.org/js/telegram-widget.js?22';

export interface SessionInfo {
  mode: 'telegram' | 'none';
  authenticated: boolean;
  unauthenticatedMode: boolean;
  user: {
    telegramUserId: string;
    username: string | null;
    firstName: string | null;
    expiresAt: string;
  } | null;
  loginWidgetReady: boolean;
  allowlistConfigured: boolean;
}

/** What Telegram hands `data-onauth`. Everything but `id`/`auth_date`/`hash` is optional. */
type TelegramUser = Record<string, string | number>;

declare global {
  interface Window {
    onTelegramAuth?: (user: TelegramUser) => void;
  }
}

function TelegramLoginButton(): ReactNode {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const authenticate = useCallback(async (user: TelegramUser): Promise<void> => {
    setError(null);
    setSubmitting(true);
    try {
      await apiPost('/api/auth/telegram', user);
      // The session cookie is set by that response. A full reload is the
      // honest way to re-enter the app: every page refetches against the new
      // session instead of trusting client state that was built while logged out.
      window.location.assign('/');
    } catch (err) {
      // The backend's own words. `not_allowlisted` and `bad_hash` are different
      // problems for a human and must not be flattened into "login failed".
      setError(
        err instanceof ApiError || err instanceof Error
          ? err.message
          : 'the login request failed before it reached the server',
      );
      setSubmitting(false);
    }
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (container === null) return;

    window.onTelegramAuth = (user: TelegramUser) => {
      void authenticate(user);
    };

    const script = document.createElement('script');
    script.src = WIDGET_SRC;
    script.async = true;
    script.setAttribute('data-telegram-login', BOT_USERNAME);
    script.setAttribute('data-size', 'large');
    script.setAttribute('data-radius', '8');
    // Asked for because the digest path will later message the signed-in user;
    // Telegram shows it as a checkbox the user can decline, and declining still
    // logs in.
    script.setAttribute('data-request-access', 'write');
    script.setAttribute('data-onauth', 'onTelegramAuth(user)');
    script.onerror = () => {
      setError(
        'telegram.org could not be reached, so the sign-in button could not load. ' +
          'Check the network, then reload.',
      );
    };
    container.appendChild(script);

    return () => {
      container.replaceChildren();
      delete window.onTelegramAuth;
    };
  }, [authenticate]);

  return (
    <div>
      {/*
        The widget's iframe is ~240 px wide and does not shrink. `overflow-x-auto`
        keeps it from pushing the card wider than a 390 px viewport if Telegram
        ever changes that; `flex justify-center` keeps it centred when it fits.
      */}
      <div ref={containerRef} id="telegram-login-widget" className="flex justify-center overflow-x-auto" />
      {submitting && (
        <p className="mt-3 text-center text-xs text-ink-3">signing in…</p>
      )}
      {error !== null && (
        <p className="mt-3 text-xs break-words text-bad" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

export function LoginPage({ session }: { session: SessionInfo | null }): ReactNode {
  return (
    // `w-full max-w-md` with border-box padding: at 390 px the card is the
    // viewport minus the gutters, and nothing inside it may exceed that — every
    // long identifier below is `break-words`, which is what actually stops the
    // horizontal scrollbar this page used to have.
    <div className="mx-auto w-full max-w-md space-y-4 px-4 py-12">
      <div className="text-center">
        <h1 className="text-lg font-semibold">mount-watch</h1>
        <p className="text-sm text-ink-3">Mount Hetzner cluster monitoring</p>
      </div>

      <Card title="Sign in with Telegram">
        {session === null ? (
          <p className="text-sm text-ink-3">checking the session…</p>
        ) : session.loginWidgetReady ? (
          <TelegramLoginButton />
        ) : (
          <Placeholder tone="warn" title="the login widget cannot be shown yet">
            {!session.allowlistConfigured
              ? 'MW_UI_TELEGRAM_ALLOWLIST is empty, so no Telegram user can sign in. Set it to the allowed user ids.'
              : 'MW_UI_TELEGRAM_BOT_TOKEN is unset, so a Login Widget signature could not be verified.'}
          </Placeholder>
        )}
      </Card>

      <p className="px-1 text-xs break-words text-ink-3">
        Only Telegram accounts on this deployment&rsquo;s allowlist can sign in. Signing in creates
        a session cookie on this host; a restart of the service ends every session.
      </p>
    </div>
  );
}
